import {getCurrencyForCountry} from '../../../../features/CatalogGrid/utils/getPriceForCountry'
import {
  buildSalesAttributionFields,
  buildSendlinkSubscriptionGroups,
} from '../../../../features/CatalogGrid/utils/buildSendLinkSubscriptionGroups'
import {KEAP_ADDONS_PARENT_CODES} from '../../../../utils/constants'
import {getRelevantLocations} from './buildCreateOrderPayload'

function tagLocation(items, locationThryvId) {
  return items.map(item => ({...item, locationThryvId}))
}

// mcDomain/keapDomain/keapTrailId/tlDomain/seoProposalId/seoDomain/
// thryvLeadsId/cohortEaid are all derived by buildSendlinkSubscriptionGroups
// from this same location's items — reused here instead of re-deriving them
// from the raw items a second time.
function couponCodesOf(loc) {
  return [
    ...new Set(
      (loc.coupons ?? []).map(c => c.code ?? c.couponCode).filter(Boolean),
    ),
  ]
}

function extractLocationDetail(loc, subscriptionCustomFields) {
  return {
    locationThryvId: loc.thryvId,
    // This location's own coupons, so the payment-link page can show them
    // inside the location instead of one order-level list.
    coupons: couponCodesOf(loc),
    mcDomain: subscriptionCustomFields.mcDomain,
    keapDomain: subscriptionCustomFields.keapDomain,
    keapTrailId: subscriptionCustomFields.keapTrailId,
    tlDomain: subscriptionCustomFields.tlDomain,
    seoProposalId: subscriptionCustomFields.seoProposalId,
    seoDomain: subscriptionCustomFields.seoDomain,
    thryvLeadsId: subscriptionCustomFields.thryvLeadsId,
    cohortEaid: subscriptionCustomFields.cohortEaid,
    isCohort: subscriptionCustomFields.isCohort,
    onboardingApptId: loc.onboardingApptId,
    thryvSuccessServicesApptId: loc.thryvSuccessServicesApptId,
  }
}

/**
 * Builds the account info fields for the cc/order/send-link payload: name,
 * email, company, currency, mailing address, and phone. Mirrors
 * ClientProposalFlow's client-payment.js buildAccountInfoFields, using this
 * flow's identity (accountInfo, or the first relevant location's
 * accountInfo) in place of Recurly account info + proposalInfo.
 *
 * @param {object} identity - the account/location identity to build from
 * @param {object} fallbackIdentity - per-field fallback when identity lacks a value
 * @param {object} contactName - {firstName, lastName} from resolveContactName
 * @returns {object} {firstName, lastName, email, company, currency,
 *   businessAddress, contactPhone}
 */
function buildAccountInfoFields(identity, fallbackIdentity, contactName) {
  const valueFor = key => {
    const primary = asText(identity?.[key])
    return primary || asText(fallbackIdentity?.[key])
  }
  const country = valueFor('country') || 'US'
  // Account Setup can retain Google Places/select values as objects (for
  // example {label, value}) while cc/order/send-link validates every address
  // field as a string. Normalize at this boundary so the payload is valid
  // regardless of which Account Setup control supplied the value.
  function asText(value) {
    if (value == null) return ''
    if (typeof value === 'string' || typeof value === 'number') {
      return String(value).trim()
    }
    if (typeof value === 'object') {
      return String(
        value.street ??
          value.address ??
          value.value ??
          value.label ??
          value.name ??
          '',
      ).trim()
    }
    return ''
  }

  return {
    // contactName already resolves first/last across the identity and the
    // order's locations (see resolveContactName); asText keeps them strings.
    firstName: asText(contactName?.firstName) || valueFor('firstName'),
    lastName: asText(contactName?.lastName) || valueFor('lastName'),
    email: valueFor('email'),
    company: valueFor('businessName'),
    currency: getCurrencyForCountry(country),
    businessAddress: {
      street: valueFor('address'),
      city: valueFor('city'),
      state: valueFor('state'),
      postalCode: valueFor('postCode'),
      country,
      phone: valueFor('phone') || valueFor('mobile'),
    },
    contactPhone: valueFor('mobile') || valueFor('phone'),
  }
}

/**
 * The contact first/last name for the order. send-link requires both, and a
 * combined parentPay order uses the parent account as its identity — but the
 * billing-service parent record carries no first/last name (only each
 * location's own contact does, see purchaseLinkState.js's buildAccountInfo).
 * So fall back, in order, to the first location in the order that has one.
 * Each name is resolved independently so a partially filled contact still works.
 */
export function resolveContactName(identity, relevantLocations = []) {
  const fromLocation = key =>
    relevantLocations.find(loc => loc?.accountInfo?.[key])?.accountInfo[key]
  return {
    firstName: identity?.firstName || fromLocation('firstName') || '',
    lastName: identity?.lastName || fromLocation('lastName') || '',
  }
}

/**
 * Cart items are normalized by productSelectionRules.js's toApiItem before
 * they reach the purchase-link state, and that drops `parentProduct`. Without
 * it buildSubscriptionGroups can't tell a Keap add-on (Additional Contacts,
 * Additional User, Text & Voice, Domain Based Gmail, Message Bundle, ...) belongs
 * under the Keap Add-Ons wrapper plan, so it nests them under the marketplace
 * plan instead — where Recurly has those add-ons deactivated/unpriced, so the
 * client payment page shows $0.00 (and tier quantities are lost).
 *
 * Restore the link from the product catalog (the same `parentProduct` source the
 * rep-side payment preview uses — see buildPaymentPreviewPayload's
 * findKeapAddonChildCodes). Scoped to Keap wrapper children only so no other
 * nesting rule changes.
 */
export function withKeapParentProduct(items = [], productConfigInfo) {
  const keapParentByCode = new Map(
    (productConfigInfo?.apps ?? [])
      .filter(app => KEAP_ADDONS_PARENT_CODES.includes(app.parentProduct))
      .map(app => [app.code, app.parentProduct]),
  )
  if (!keapParentByCode.size) return items
  return items.map(item =>
    item.parentProduct || !keapParentByCode.has(item.productCode)
      ? item
      : {...item, parentProduct: keapParentByCode.get(item.productCode)},
  )
}

export function buildSendLinkPayload({
  accountInfo,
  locations,
  orderMode,
  selectedLocationId,
  orderId,
  sendLinkVia = 'email',
  // The logged-in rep creating this order — PurchaseLinkSlice's own
  // salesRepId/userRole (sibling to accountInfo, not nested inside it or
  // any location's own accountInfo — see PurchaseLinkSlice.js's comment).
  salesRepId,
  userRole,
  // The order's sales/partner attribution — PurchaseLinkSlice's
  // salesAttributionFields, captured by the "Create Purchase Link" modal
  // (AccountEditModal) and committed once at Save/Continue time.
  salesAttributionFields,
  // PurchaseLinkSlice's productConfigInfo (the product catalog) — used to
  // restore each Keap add-on's parentProduct, see withKeapParentProduct.
  productConfigInfo,
}) {
  const relevantLocations = getRelevantLocations({
    locations,
    orderMode,
    selectedLocationId,
  })

  const identity =
    orderMode === 'parentPay'
      ? accountInfo
      : relevantLocations[0]?.accountInfo ?? accountInfo

  // Returns {firstName, lastName, email, company, currency, businessAddress,
  // contactPhone} — spread into the returned payload below.
  const accountInfoFields = buildAccountInfoFields(
    identity,
    relevantLocations[0]?.accountInfo,
    resolveContactName(identity, relevantLocations),
  )

  // Each location's buildSendlinkSubscriptionGroups result is split the
  // same way as ClientProposalFlow's client-payment.js: parentSubscriptions/
  // childSubscriptions/oneTimeFees are tagged with the location's thryvId
  // here and merged into the top-level arrays below; subscriptionCustomFields
  // (isCohort/keapAccount/keapId/isInvoiceBilled, and any of mcDomain/
  // keapDomain/keapTrailId/tlDomain/seoProposalId/seoDomain/thryvLeadsId/
  // cohortEaid that apply) stays per location and feeds locationDetails via
  // extractLocationDetail.
  const perLocationGroups = relevantLocations.map(loc => {
    const {
      parentSubscriptions,
      childSubscriptions,
      oneTimeFees,
      subscriptionCustomFields,
    } = buildSendlinkSubscriptionGroups(
      withKeapParentProduct(loc.items ?? [], productConfigInfo),
    )
    return {
      loc,
      parentSubscriptions: tagLocation(parentSubscriptions, loc.thryvId),
      childSubscriptions: tagLocation(childSubscriptions, loc.thryvId),
      oneTimeFees: tagLocation(oneTimeFees, loc.thryvId),
      subscriptionCustomFields,
    }
  })
  const parentSubscriptions = perLocationGroups.flatMap(
    g => g.parentSubscriptions,
  )
  const childSubscriptions = perLocationGroups.flatMap(
    g => g.childSubscriptions,
  )
  const oneTimeFees = perLocationGroups.flatMap(g => g.oneTimeFees)
  return {
    appUrl: window.location.origin,
    orderHeaderId: orderId,
    flow: 'new',
    repId: salesRepId,
    repRole: userRole,
    thryvId: identity?.thryvId,
    sendLinkVia,
    isCohort: false,
    keapAccount: false,
    keapId: '',
    isInvoiceBilled: ['AU', 'NZ'].includes(identity?.country || 'US'),

    ...accountInfoFields,
    parentSubscriptions,
    childSubscriptions,
    oneTimeFees,
    // One entry per distinct code: every location can carry the same coupon
    // (e.g. a default waiver), and the order stores coupons per order, not per
    // location.
    coupons: [
      ...new Set(
        relevantLocations.flatMap(loc =>
          (loc.coupons ?? []).map(c => c.code ?? c.couponCode).filter(Boolean),
        ),
      ),
    ],

    // One entry per location (see extractLocationDetail) — persisted to
    // order_location_detail so a combined multi-location order's Recurly
    // custom fields and appointment bookings use each location's own values
    // instead of a single value shared across every location.
    locationDetails: perLocationGroups
      .map(({loc, subscriptionCustomFields}) =>
        extractLocationDetail(loc, subscriptionCustomFields),
      )
      .filter(d => Boolean(d.locationThryvId)),

    ...buildSalesAttributionFields(salesAttributionFields),
  }
}
